/**
 * ReauthPanel — the unauthenticated SHELL surface, rendered in-app when a session has ended while
 * the user is still on a feature route (status !== "authenticated"). It is the feature-local twin of
 * the route-level /login page: a centered card hosting the reusable LoginForm. On success the auth
 * store flips to "authenticated" and the host screen re-renders its real content. Keyboard-first;
 * RTL-aware via logical layout.
 */
import { Card } from "@/ui";
import { useT } from "@/app";
import { LoginForm } from "./LoginForm";

export interface ReauthPanelProps {
  /** Heading copy — defaults to the expired-session framing. */
  title?: string;
  description?: string;
  onSuccess?: () => void;
}

export function ReauthPanel({ title, description, onSuccess }: ReauthPanelProps) {
  const { t } = useT();
  return (
    <div
      role="region"
      aria-label={t("auth.session.expired.title")}
      style={{
        display: "grid",
        placeItems: "center",
        minBlockSize: "60vh",
        padding: "var(--nv-s-5)",
      }}
    >
      <Card
        title={title ?? t("auth.session.expired.title")}
        style={{ inlineSize: "min(420px, 100%)" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <p style={{ margin: 0, color: "var(--nv-slate)" }}>
            {description ?? t("auth.session.expired.desc")}
          </p>
          <LoginForm {...(onSuccess ? { onSuccess } : {})} />
        </div>
      </Card>
    </div>
  );
}
